import { ReactNode } from 'react'
import { AtlasObjectType, IAtlasObjectRef } from '@common/helpers/atlasUrls'
import {
  RESTRICTED_LINK_LABEL,
  atlasObjectRefKey,
  isResolvedPreview,
} from '@common/interfaces/atlasObjects'
import { PhotoSubjectType } from '@common/interfaces/photo'
import cn from 'classnames'
import { useAtlasObjectPreviews } from '../../../hooks/useAtlasObjectPreviews'
import AtlasRecordChip from '../AtlasRecordChip'
import ProfilePhoto from '../ProfilePhoto'
import linkStyles from '../RichTextShared/linkStyles.module.scss'
import styles from './ObjectLinkChip.module.scss'

/** Tasks render through TaskLinkChip — they need a live status glyph, not an image. */
export type AtlasRecordType = Exclude<AtlasObjectType, AtlasObjectType.Task>

interface IProps {
  objectRef: IAtlasObjectRef & { type: AtlasRecordType }
  url: string
  /**
   * What the content already shows for this link: the address itself on the
   * Draft path, the author's label on Arc's. Rendered while the record is still
   * resolving, and for good if it never does — an id that points at nothing must
   * not become an empty chip.
   */
  children: ReactNode
  className?: string
  /**
   * Whether `children` is the address itself. Only then may the unresolved
   * fallback be clipped: a label the writer chose is words, and cutting those
   * hides what the link is for (see linkStyles).
   */
  childrenAreAddress?: boolean
  /**
   * Render `children` as the label instead of the resolved name. Required inside
   * an editable editor, where the chip sits on an entity whose text Draft maps
   * the caret through — replacing it with our own string strands the cursor.
   */
  labelFromChildren?: boolean
}

/**
 * Types whose link id is also the id the photo store keys on, so ProfilePhoto
 * can fetch the face or mark itself and fall back to initials.
 *
 * Absent on purpose: Prospect, BBC, DueDiligence and OngoingReporting are keyed
 * on a reporting row or a borrowing base rather than a photo subject, and the
 * mark they show belongs to the client behind them, which the preview already
 * resolves into `imageUrl`. TaskProject and Participant have no mark at all.
 */
export const PHOTO_SUBJECTS: Partial<Record<AtlasRecordType, PhotoSubjectType>> = {
  [AtlasObjectType.Person]: PhotoSubjectType.People,
  [AtlasObjectType.Client]: PhotoSubjectType.Clients,
  [AtlasObjectType.Organization]: PhotoSubjectType.Organizations,
  [AtlasObjectType.Entity]: PhotoSubjectType.Entities,
  [AtlasObjectType.Company]: PhotoSubjectType.Company,
  [AtlasObjectType.User]: PhotoSubjectType.Users,
}

/**
 * An Atlas link rendered as the record it points at: image or type icon, plus
 * the record's name, previewing the record on hover and click.
 *
 * The name is resolved per reader at render time rather than stored with the
 * message — that is what lets the same message show a name to one colleague and
 * `<restricted link>` to another, and what keeps a renamed record current in an
 * old conversation. The message itself still holds the address that was pasted.
 */
const ObjectLinkChip = ({
  objectRef,
  url,
  children,
  className,
  childrenAreAddress = false,
  labelFromChildren = false,
}: IProps) => {
  const previewsByKey = useAtlasObjectPreviews([objectRef])
  const preview = previewsByKey[atlasObjectRefKey(objectRef)]

  // Still in flight, or an id that resolves to nothing — a deleted record, a
  // person with no name on file, a request that failed. Still a link: this
  // decorator claims the range before auto-linkify, so rendering bare text here
  // would leave the address permanently unclickable, which is worse than the
  // plain link it replaced.
  if (!preview) {
    return (
      <AtlasRecordChip
        objectRef={objectRef}
        url={url}
        className={cn(linkStyles.textLink, childrenAreAddress && linkStyles.bareAddress, className)}
        title={url}
        isBare
      >
        {children}
      </AtlasRecordChip>
    )
  }

  // Deliberately not a link: following it would only land on a page this reader
  // is not allowed to open.
  if (!isResolvedPreview(preview)) {
    return (
      <span className={cn(styles.restricted, className)} title={RESTRICTED_LINK_LABEL}>
        {labelFromChildren ? children : RESTRICTED_LINK_LABEL}
      </span>
    )
  }

  const photoSubject = PHOTO_SUBJECTS[objectRef.type]

  return (
    <AtlasRecordChip objectRef={objectRef} url={url} className={className} title={preview.name}>
      <ProfilePhoto
        name={preview.name}
        subjectType={photoSubject}
        id={photoSubject ? objectRef.id : undefined}
        iconUrl={preview.imageUrl}
        // Prospect and BBC carry a client's mark with no subjectType to derive
        // the shape from, so it would be cropped into the circle a face gets.
        isLogo={objectRef.type !== AtlasObjectType.Person}
        size="xsmall"
        className={styles.avatar}
      />
      {labelFromChildren ? children : preview.name}
    </AtlasRecordChip>
  )
}

export default ObjectLinkChip
